"use client";
import React, { useEffect, useState } from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { Pie } from "./Global/Pie";
import { setAlertData, useLazyGetOrderTrackingInfoQuery } from "@/store";
import { useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { Spinner } from "./Global/Spinner";
import { useTranslations } from "next-intl";

const stepsData = [
  {
    name: "Step 1",
    status: "complete",
    percentage: 100,
    devName: "Sabina G.",
    devJob: "Designer",
    colorCompleted: "#22BFA5",
    colorProgress: "#fc5185",
  },
  {
    name: "Step 2",
    status: "current",
    percentage: 90,
    devName: "Fuad M.",
    devJob: " Frontend Developer",
    colorCompleted: "#22BFA5",
    colorProgress: "#fc5185",
  },
  {
    name: "Step 3",
    status: "upcoming",
    percentage: 65,
    devName: "Ahmad R.",
    devJob: "Backend Developer",
    colorCompleted: "#22BFA5",
    colorProgress: "#fc5185",
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const OrderTracker = () => {
  const t = useTranslations();
  const [trackingId, setTrackingId] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const dispatch = useDispatch();
  const [getOrderTrackingInfo, { data, isError, error, isSuccess }] =
    useLazyGetOrderTrackingInfoQuery();
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await getOrderTrackingInfo(trackingId);
    } catch (error) {
      dispatch(
        setAlertData({ status: "error", message: "Your uuid is wrong" })
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isSuccess) {
      router.push(`/order-tracker?uuid=${trackingId}`);
    }
  }, [isSuccess, dispatch]);

  const renderedSteps = stepsData.map((data, index) => (
    <div
      key={index}
      className="overflow-hidden rounded-lg bg-white px-4 py-5 sm:p-6 flex flex-col items-center justify-between h-[280px] shadow-4 shadow-[#000000]/20"
    >
      <dt className="truncate text-center text-sm font-medium text-gray-500">
        {data.percentage === 100 ? t("completed") : t("progress")}
      </dt>
      <dd className="mt-1 font-semibold tracking-tight text-gray-600">
        <Pie
          percentage={data.percentage}
          colour={
            data.percentage === 100 ? data.colorCompleted : data.colorProgress
          }
        />
      </dd>
      <dd className="mt-1 md:text-xl text-sm font-bold tracking-tight text-gray-800 whitespace-nowrap">
        {data.devName}
      </dd>
      <dd className="mt-1 text-sm font-semibold tracking-tight text-gray-400">
        {data.devJob}
      </dd>
    </div>
  ));

  return (
    <section
      id="order-tracking"
      className="lg:mt-20 md:mt-12 sm:mt-4 mt-0 md:py-8 sm:py-4 py-2 px-4 lsm:min-h-[500px] min-h-[400px] flex flex-col items-center xlg:flex-row gap-y-16 xlg:gap-y-0 mx-auto max-w-7xl"
    >
      <div className="leftSection w-full xlg:w-1/2 hidden xmd:block">
        <dl className="mt-5 grid grid-cols-1 gap-5 xmd:grid-cols-3">
          {renderedSteps}
        </dl>

        <nav aria-label="Progress" className="mt-8">
          <ol
            role="list"
            className="flex items-center sm:ml-8 xmd:ml-20 md:ml-16 lmd:ml-28 xlg:ml-12 lg:ml-12 xl:ml-20 xl2:ml-24"
          >
            {stepsData.map((step, stepIdx) => (
              <li
                key={step.name}
                className={classNames(
                  stepIdx !== stepsData.length - 1 ? "w-2/5" : "",
                  "relative "
                )}
              >
                {step.status === "complete" ? (
                  <>
                    <div
                      className="absolute inset-0 flex items-center"
                      aria-hidden="true"
                    >
                      <div className="h-0.5 w-full bg-mainLightPurple" />
                    </div>
                    <p className="relative flex h-8 w-8 items-center justify-center rounded-full bg-mainLightPurple hover:bg-mainLightPurpleHover">
                      <CheckIcon
                        className="h-5 w-5 text-white"
                        aria-hidden="true"
                      />
                    </p>
                  </>
                ) : step.status === "current" ? (
                  <>
                    <div
                      className="absolute inset-0 flex items-center"
                      aria-hidden="true"
                    >
                      <div className="h-0.5 w-full bg-gray-200" />
                    </div>
                    <p
                      className="relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-mainLightPurple bg-white"
                      aria-current="step"
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full bg-mainLightPurple"
                        aria-hidden="true"
                      />
                    </p>
                  </>
                ) : (
                  <>
                    <div
                      className="absolute inset-0 flex items-center"
                      aria-hidden="true"
                    >
                      <div className="h-0.5 w-full bg-gray-200" />
                    </div>
                    <p className="group relative flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white hover:border-gray-400">
                      <span
                        className="h-2.5 w-2.5 rounded-full bg-transparent group-hover:bg-gray-300"
                        aria-hidden="true"
                      />
                    </p>
                  </>
                )}
              </li>
            ))}
          </ol>
          <div className="mt-4 ml-0 lmd:ml-6 xl:ml-4 flex flex-row justify-between text-gray-500 font-semibold ">
            <span className="w-1/3 text-center">Web Design</span>
            <span className="w-1/3 text-center">Back-end</span>
            <span className="w-1/3 text-center">Front-end</span>
          </div>
        </nav>
      </div>

      <div className="rightSection w-full xlg:w-1/2 flex justify-center items-center">
        <div className=" xlg:w-[75%] w-full h-[388px] sm:px-8 px-4 sm:py-10 py-6 bg-white rounded-xl flex flex-col gap-4 justify-between items-center">
          <h2 className="font-bold  text-[25px] text-center leading-[32px] max-w-70">
            {t("orderTrackingAll")}
          </h2>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
            <input
              placeholder={t("orderNumber")}
              name="order_id"
              type="text"
              onChange={(e) => setTrackingId(e.target.value)}
              className=" h-13 border border-[#676767] rounded-xl text-center focus:outline-none text-[#676767] placeholder:text-[#676767] placeholder:font-semibold"
            />
            {(isError || error) && (
              <span className="text-sm text-red-500">{t("wrongUUID")}</span>
            )}
            <button className="bg-mainLightPurple hover:bg-mainLightPurpleHover text-white h-13 rounded-xl shadow-4 shadow-mainLightPurple">
              {loading ? (
                <Spinner />
              ) : (
                <span className="text-lg font-semibold">{t("track")}</span>
              )}
            </button>
          </form>

          <span className="text-xs text-center text-gray-500">
            {t("orderDesc")}{" "}
          </span>
        </div>
      </div>
    </section>
  );
};